import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import * as styles from './removed-candidates.module.scss';
import {
  decodeBase64ToImage,
  handleShowCT,
  handleShowRetirementDate,
  handleShowTextTooltip,
  handleShowTooltip,
  handleShowYIPAndYISG,
  hideOtherTalentLabel,
} from '../../../../utils/helper';
import { Link } from 'react-router-dom/cjs/react-router-dom';
import { Tooltip } from 'antd';
import { ApprovedLabel } from '../../../Meeting/AgendaDetails/components/Recommendations/Box/ApprovedLabel';
import moment from 'moment';
import { in_active_icon, red_dot, suspended_icon, warning_dot } from '../../../../assets/img';
import { chunk, unionBy } from 'lodash';
import { NOTIFICATION_MEETING_TYPE } from '../../../../utils/constants';
import queryString from 'query-string';
import AvatarStack from '../../../../components/AvatarStack/AvatarStack';

const MAX_HEIGHT_CARD = 450;
const RemovedCandidates = ({ positions = [], incumbents }) => {
  const [open, setOpen] = useState(false);

  const types = useMemo(() => {
    const { successor_type } = queryString.parse(window.location.search);
    return successor_type ? [successor_type] : [NOTIFICATION_MEETING_TYPE.SUCCESSOR_ATTRITION, NOTIFICATION_MEETING_TYPE.SUCCESSOR_PROMOTION];
  }, [window.location.search]);

  const cards = useMemo(() => {
    const allCards = unionBy(
      positions?.filter((item) => types.includes(item?.successorType)),
      'staff_id'
    );
    return {
      all: allCards || [],
      chunk: allCards.length ? chunk(allCards, 3) : [],
    };
  }, [positions]);

  const avatars = useMemo(() => {
    return {
      data: cards.all.map((card) => card?.image),
      total: cards.all.length || 0,
    };
  }, [cards.all]);

  const handleClickShowDetails = () => {
    setOpen(!open);
  };

  return (
    <div className={styles.wrapper} style={{ display: !avatars.total ? 'none' : 'block' }}>
      <div className={styles.title}>Removed Candidates</div>
      <div className={styles.avatars}>
        <div>
          <AvatarStack {...avatars} />
          <div>{avatars.total} candidates have been removed from this Succession Planning.</div>
        </div>
        <div onClick={handleClickShowDetails}>{open ? 'Hide Details' : 'Show Details'}</div>
      </div>
      <div className={styles.cardsContainer} style={{ maxHeight: !open ? 0 : cards.chunk.length * MAX_HEIGHT_CARD }}>
        {cards.chunk.map((items, idx) => {
          return (
            <div key={idx} className={styles.cards}>
              {items.map((item, index) => (
                <CardBox key={index} item={item} index={index + 3 * idx} incumbents={incumbents} />
              ))}
              {items.length === 1 && (
                <>
                  <td></td>
                  <td></td>
                </>
              )}
              {items.length === 2 && <td></td>}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const CardBox = (props) => {
  const { item, index, incumbents } = props;
  const actionNameColor = item?.successorType === NOTIFICATION_MEETING_TYPE.SUCCESSOR_ATTRITION ? '#EBEBEB' : '#9FDEDB';

  return (
    <div className={`${styles.card_box} card-dsp`} data-testid="view-card-box">
      <div style={{ background: actionNameColor }} className={styles.actionType}>
        {item?.successorType === NOTIFICATION_MEETING_TYPE.SUCCESSOR_ATTRITION ? item?.action_name : 'Promoted'}
      </div>
      <div className="d-flex no-gutters justify-content-start align-items-stretch w-100" style={{ gap: '10px' }}>
        <div className={`${styles.left}`}>
          <div className="row flex-column no-gutters align-items-start h-100">
            <div className={styles.number}>{index + 1}</div>
            <div className={styles.image}>
              <img src={decodeBase64ToImage(item?.image)} alt="" />
              {['INACTIVE', 'SUSPENDED'].includes(item?.assignment_status_type) && (
                <Tooltip
                  placement="top"
                  title={
                    <div
                      style={{
                        padding: '5px',
                      }}
                    >
                      <div
                        style={{
                          color: '#F3F3F3',
                          fontFamily: 'Inter',
                          fontSize: '12px',
                          fontWeight: 600,
                          marginBottom: '10px',
                        }}
                      >
                        {item?.assignment_status_type === 'INACTIVE' ? 'In-active' : 'Suspended'}
                      </div>
                      <div
                        className="d-flex justify-content-start align-items-center"
                        style={{
                          gap: '10px',
                        }}
                      >
                        <img src={item?.assignment_status_type === 'INACTIVE' ? red_dot : warning_dot} alt="icon" />
                        <div
                          style={{
                            color: '#F3F3F3',
                            fontFamily: 'Inter',
                            fontSize: '12px',
                            fontWeight: 400,
                          }}
                        >
                          {item?.action_name} {item?.action_reason && `(${item?.action_reason})`}
                        </div>
                      </div>
                    </div>
                  }
                  color={'black'}
                  key={'black'}
                  overlayInnerStyle={{ borderRadius: '6px' }}
                  data-testid="show-tooltip"
                >
                  <div style={{ position: 'absolute', bottom: 0, right: 0 }}>
                    <img
                      src={item?.assignment_status_type === 'INACTIVE' ? in_active_icon : suspended_icon}
                      style={{ height: '15px', width: '15px' }}
                      alt="icon"
                    />
                  </div>
                </Tooltip>
              )}
            </div>
          </div>
        </div>

        <div className={`${styles.right}`}>
          <div className="d-flex no-gutters justify-content-between align-items-stretch">
            <div className={styles.name}>
              <Link to={`/profile/${item?.staff_id}`} target="_blank" className={styles.recommendation__name} draggable="false">
                {item?.birth_name || item?.candidate_name}
              </Link>
              <div className={styles.recommendation__name}>
                {!hideOtherTalentLabel(item?.assignment_status_type, item?.role_level, item?.top_talent_status) &&
                  !['119463', '1032501'].includes(item?.staff_id) &&
                  handleShowCT(item?.top_talent_status)}
              </div>
              <div className={styles.recommendation__name}>{item?.percentage ? `${item?.percentage}%` : ''}</div>
            </div>
          </div>

          <div className="mb-1">
            <span style={{ fontWeight: '500', fontSize: '14px', lineHeight: '14px', color: '#3d3d3d' }}>{item?.position_name}</span>
          </div>
          <div style={{ fontSize: '12px' }}>
            <div className="mb-1">
              <span>Business: </span>
              <span style={{ fontWeight: '500' }}>{item?.business_unit}</span>
            </div>
            <div className="mb-1">
              <span>Sector: </span>
              <span style={{ fontWeight: '500' }}>{item?.sector}</span>
            </div>
            <div className="mb-1">
              <span>OPU: </span>
              <span style={{ fontWeight: '500' }}>{item?.opu}</span>
            </div>
            <div className="mb-1">
              <span>Division: </span>
              <span style={{ fontWeight: '500' }}>{item?.division}</span>
            </div>
            <div className="mb-1">
              <span>Department: </span>
              <span style={{ fontWeight: '500' }}>{item?.department_name}</span>
            </div>
          </div>
          <div className="mb-1 d-flex justify-content-start" style={{ gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
            <div>
              <span>ID: </span>
              <span style={{ fontWeight: 'bold' }}> {item?.identity_number || item?.staff_id}</span>
            </div>
            <span>
              {
                <Tooltip
                  placement="bottom"
                  title={handleShowTextTooltip(item, incumbents)}
                  color={'black'}
                  key={'black'}
                  overlayInnerStyle={{ borderRadius: '6px', minWidth: 'max-content', padding: '12px', width: '100%' }}
                  className={styles.sp_counter}
                >
                  {handleShowTooltip(item, incumbents)}
                </Tooltip>
              }
            </span>
            <div>
              <ApprovedLabel positions={incumbents} item={item} />
            </div>
          </div>
        </div>
      </div>
      <div
        style={{
          border: '1px solid #CBD6E2',
          borderRadius: '6px',
          fontSize: '12px',
          padding: '10px',
        }}
      >
        <div className="mb-2 d-flex justify-content-start" style={{ gap: '20px' }}>
          <div>
            <span>YIP: </span>
            <div style={{ fontWeight: 'bold' }}>
              {handleShowYIPAndYISG(item?.years_in_position ?? item?.year_in_position, item?.date_in_position)}
            </div>
          </div>
          <div>
            <span>YISG: </span>
            <div style={{ fontWeight: 'bold' }}>
              {item?.sg} ({handleShowYIPAndYISG(item?.years_in_sg ?? item?.year_in_sg, item?.date_in_sg)})
            </div>
          </div>
        </div>
        <div className="mb-2 d-flex justify-content-start" style={{ gap: '28px' }}>
          <div>
            <span>Age: </span>
            <div style={{ fontWeight: 'bold' }}>{item?.age}</div>
          </div>
          <div>
            <span>Retirement: </span>
            <div style={{ fontWeight: 'bold' }}>
              {handleShowRetirementDate(item.retirement_date)}
              {item?.retirement_date && (item?.dateOfBirth || item?.birth_date)
                ? `(${Number(moment(item?.retirement_date).format('YYYY')) - Number(moment(item?.dateOfBirth).format('YYYY'))})`
                : ''}
            </div>
          </div>
          <div className="mb-2">
            <span>Performance Rating: </span>
            <div style={{ fontWeight: 'bold' }}>{item?.performance_rating}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

CardBox.propTypes = {
  item: PropTypes.object,
  index: PropTypes.number,
  incumbents: PropTypes.array,
};

export default RemovedCandidates;

RemovedCandidates.propTypes = {
  cards: PropTypes.array,
};
